import { useEffect, useRef } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { useAtomValue } from "jotai";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { useToast } from "@humansignal/ui";
import { projectAtom } from "../providers/ProjectProvider";
import { routePolicies } from "./routePolicy";

/**
 * One-stop route access control.
 *
 * Evaluates ``routePolicies`` on every navigation + context change
 * (user, project). On deny it calls ``history.replace(redirect)`` and
 * pops a toast explaining why. Page components no longer need their
 * own "if not authorized → redirect" ``useEffect`` — mount this once
 * near the root of the app and the policy map is the single source
 * of truth for who can see what.
 *
 * Policy matching is first-match-wins, so the patterns in
 * ``routePolicies`` should go from most-specific to least-specific
 * (this is already the case there).
 */
export const RouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const project = useAtomValue(projectAtom);
  const location = useLocation();
  const history = useHistory();
  const toast = useToast();
  // Suppress repeat redirects if the policy keeps firing in a loop
  // (shouldn't happen in practice but defends against a bug).
  const lastRedirect = useRef<string | null>(null);
  // One server reconcile per path while user is null (see below).
  const reconciled = useRef<string | null>(null);

  // Dead-session gate. The boot pivot in main.tsx only runs at cold
  // start; a session that dies MID-FLIGHT (tokens expired or cleared)
  // used to leave the authed shell rendered with skeletons that never
  // fill and a "Sign in" button in the menubar — two sources of truth
  // disagreeing on one screen. JWT hosts only (Capacitor/desktop
  // shell); the browser web app is cookie-authed, its 401 handler
  // owns this.
  useEffect(() => {
    if (user) return;
    if (location.pathname.startsWith("/auth/")) return;
    try {
      if (typeof window === "undefined" || typeof (window as any).Capacitor === "undefined") return;
      const live = (key: string): boolean => {
        const tok = window.localStorage.getItem(key);
        if (!tok) return false;
        try {
          let b = tok.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
          while (b.length % 4) b += "=";
          const claims = JSON.parse(atob(b)) as { exp?: number };
          return typeof claims.exp === "number" && claims.exp > Math.floor(Date.now() / 1000);
        } catch {
          return false;
        }
      };
      if (!live("afri:jwt:access") && !live("afri:jwt:refresh")) {
        window.history.replaceState({}, "", "/auth/login");
        window.dispatchEvent(new PopStateEvent("popstate"));
        return;
      }
      // Tokens LOOK live locally but the shell still has no user —
      // reconcile with the SERVER once. Locally-unexpired tokens the
      // server rejects (secret rotation, revocation, deleted user)
      // used to leave the authed shell with skeleton cards and a
      // "Sign in" button forever. 401 → clear + login. Network
      // errors keep the session (offline home is legitimate).
      if (reconciled.current === location.pathname) return;
      reconciled.current = location.pathname;
      void (async () => {
        try {
          const tok = window.localStorage.getItem("afri:jwt:access");
          const base =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).APP_SETTINGS?.hostname || "";
          const r = await fetch(`${base}/api/current-user/whoami`, {
            headers: tok ? { Authorization: `Bearer ${tok}` } : undefined,
            cache: "no-store",
          });
          if (r.status === 401 || r.status === 403) {
            window.localStorage.removeItem("afri:jwt:access");
            window.localStorage.removeItem("afri:jwt:refresh");
            window.localStorage.removeItem("afri:last-user");
            window.history.replaceState({}, "", "/auth/login");
            window.dispatchEvent(new PopStateEvent("popstate"));
          }
        } catch {
          /* offline — cached shell is fine */
        }
      })();
    } catch {
      /* storage unavailable — leave as is */
    }
  }, [user, location.pathname]);

  useEffect(() => {
    // Wait for auth to hydrate. Before ``user`` lands the policies
    // would evaluate against ``null`` and bounce authenticated
    // operators off their own pages on the first render.
    if (!user) return;

    const ctx = {
      user: {
        id: user.id,
        is_staff: user.is_staff,
        is_superuser: user.is_superuser,
        active_organization: user.active_organization,
        active_organization_role: user.active_organization_role,
      },
      project:
        project && (project as any).id
          ? {
              id: (project as any).id,
              is_published: (project as any).is_published,
              created_by: (project as any).created_by,
            }
          : null,
    };

    for (const { pattern, exact, policy } of routePolicies) {
      const match = matchPath(location.pathname, { path: pattern, exact });
      if (!match) continue;
      const result = policy(ctx);
      if (result.allow) {
        lastRedirect.current = null;
        return; // first-match-wins; allow → stop
      }
      // Guard against ping-ponging: don't issue the same redirect
      // twice in a row without a location change.
      if (lastRedirect.current === result.redirect && location.pathname === result.redirect) {
        return;
      }
      lastRedirect.current = result.redirect;
      if (result.message) {
        toast?.show?.({ message: result.message, type: "error" });
      }
      history.replace(result.redirect);
      return;
    }
  }, [location.pathname, user, project, history, toast]);

  return children as JSX.Element;
};
